/**
 * Content-area crossfade for navigation between a project's
 * sections — most notably Folders list → Folder detail.
 *
 * `ProjectDetail` stays mounted across section navigation; only its routed
 * `<Outlet />` child swaps. That makes it the natural persistent host for:
 *
 *  1. A `<LayoutGroup>`, so a section that opts into a shared `layoutId` can
 *     morph between routes rather than hard-swapping.
 *
 *  2. A keyed entrance crossfade: the incoming section content fades in as
 *     the route swaps, so the swap reads as "content changing" rather than
 *     a hard cut.
 *
 * Re-keys on `pathname` so every section navigation replays the fade.
 * Respects `prefers-reduced-motion`.
 */

import { useLocation } from 'react-router';
import { LayoutGroup, motion, useReducedMotion } from 'motion/react';
import {
  assetViewerTransition,
  sharedElementTransition,
} from '@/shared/constants/motion.constants';

interface SectionTransitionProps {
  children: React.ReactNode;
}

export const SectionTransition = ({ children }: SectionTransitionProps) => {
  const { pathname } = useLocation();
  const shouldReduceMotion = useReducedMotion();

  // Same box as the animated branch: the section below fills the height it is
  // given, so dropping the wrapper would collapse the page to its content.
  if (shouldReduceMotion) {
    return <div className="flex min-h-0 flex-1 flex-col">{children}</div>;
  }

  // Collapse the `/files/<id>` and `/files/<id>/compare` suffixes (see
  // ROUTE_PATTERNS.PROJECT_FILE_DETAIL / PROJECT_FILE_COMPARE in
  // shared/config/routes.ts) into one asset-viewer key. Folder detail keeps its
  // own key, so opening a file still fades in the viewer chrome/background;
  // sibling file changes keep `AssetDetail` mounted so its inner
  // AnimatePresence can run the horizontal glide instead of the page
  // hard-swapping.
  // The folder id is collapsed for the same reason: the rail is the section's
  // navigation, and re-keying on it would tear the tree down on every click.
  const transitionKey = pathname
    .replace(/\/files\/[^/]+(?:\/compare)?$/, '/files')
    .replace(/\/folders\/[^/]+/, '/folders');

  return (
    <LayoutGroup>
      <motion.div
        key={transitionKey}
        className="flex min-h-0 flex-1 flex-col"
        // A short drop, not just a fade: the section should feel like it lands.
        // One movement for the whole thing — the elements inside no longer
        // animate separately, which is what made this read as an assembly line.
        //
        // Downward, like every other entrance in the app. It also has to be:
        // this sits in a scroll container, and a positive offset pushes the
        // bottom edge past it for the length of the animation, so a scrollbar
        // appears and then leaves. Overflow above the start edge is clipped
        // rather than scrollable, so coming from above costs nothing.
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={
          transitionKey.endsWith('/files')
            ? assetViewerTransition.chromeFade
            : { duration: sharedElementTransition.crossfadeDuration, ease: 'easeOut' }
        }
      >
        {children}
      </motion.div>
    </LayoutGroup>
  );
};

SectionTransition.displayName = 'SectionTransition';
